import { faPlay, faStop } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ChevronDown } from "baseui/icon";
import moment from "moment/moment";
import React, {
  Dispatch,
  FC,
  MouseEvent,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Tooltip as ReactTooltip } from "react-tooltip";

import { AvatarPlaceholder } from "../../../../../../components/avatar/AvatarPlaceholder";
import {
  AvatarStack,
  AvatarStackItem,
} from "../../../../../../components/avatar/AvatarStack";
import LegacyButton from "../../../../../../components/button/Button";
import { Card } from "../../../../../../components/card/Card";
import { StyledDatePicker } from "../../../../../../components/datepicker/datepicker";
import { Button } from "../../../../../../components/newUI/button";
import { RoleMembersPreview } from "../../../../../../components/select/previews/RoleMembersPreview";
import CustomSelect from "../../../../../../components/select/Select";
import { CheckSvg } from "../../../../../../components/svg/CheckSvg";
import { CloseSvg } from "../../../../../../components/svg/CloseSvg";
import IndividualContactSvg from "../../../../../../components/svg/IndividualContactSvg";
import {
  getInitials,
  getInitialsFromFullName,
} from "../../../../../../constants/constants";
import {
  RoleModel,
  UserRoleModel,
} from "../../../../../../factories/role/role.model";
import {
  WorkDetailsModel,
  WorkJobAssignee,
} from "../../../../../../factories/work/model/work-details.model";
import { WorkTaskModel } from "../../../../../../factories/work/model/work-task.model";
import { useAppSelector } from "../../../../../../redux/redux";
import { getLinkedFirm } from "../../../../../../sessionStorage/sessionStorage";
import { useGetRolesQuery } from "../../../../../../slices/RoleSlice";
import { useGetUsersQuery } from "../../../../../../slices/UserSlice";
import {
  useUpdateWorkAssigneesMutation,
  useUpdateWorkDatesMutation,
  useUpdateWorkRoleMutation,
} from "../../../../../../slices/WorkSlice";
import { useGetWorkTasksQuery } from "../../../../../../slices/WorkTasksSlice";
import { SelectOptionType } from "../../../../../../types/types";
import { navigateToContact } from "../../../../../contacts/utils/navigateToContacts";
import AddTimer from "../../../../../main/AddTimer";
import {
  isTimerActive,
  isTimerForTarget,
} from "../../../../../main/timer/timerTarget";
import { useTimerTarget } from "../../../../../main/timer/useTimerTarget";
import { getCompletedTasks } from "../../../../utils/workCharts";
import { RecurringCard } from "../../../repeats/RecurringIndicators";
import { WorkStatus } from "../work-main/WorkStatus";
import { EditBudgetModal } from "./EditBudgetModal";

type Props = {
  work: WorkDetailsModel;
  setIsEdit: Dispatch<SetStateAction<boolean>>;
};

type WorkDateRange = {
  startDate: Date;
  dueDate: Date;
};

const DISPLAY_DATE_FORMAT = "MMM DD, YYYY";
const DATE_PICKER_FORMAT = "MMM dd, yyyy";
const START_AFTER_DUE =
  "The start date can't be after the due date. Change the due date first.";
const DUE_BEFORE_START = "The due date can't be before the start date.";
const DUE_BEFORE_SUBTASK =
  "The due date can't be before a subtask's due date. Change the subtask first.";
/** The later of two optional dates (due date box: start date and subtasks). */
const laterDate = (a?: Date | null, b?: Date | null) =>
  a && b ? (a > b ? a : b) : a || b || undefined;
const TASK_PROGRESS_COLOR = "#8BD8EC";
const MAX_ASSIGNEES = 5;
const ASSIGN_ROLE_TO_ME_TOOLTIP_ID = "assign-role-to-me-tooltip";

const CARD_CLASSES = "shadow-box px-4 max-w-full";
// Labels keep their full width (never cut off on small screens); the value
// column takes the rest.
const SECTION_GRID_CLASSES =
  "grid grid-cols-[max-content_minmax(0,1fr)] items-center gap-x-4 gap-y-4 py-4 text-base";
const LABEL_CLASSES = "whitespace-nowrap text-sm text-[#2B333CCC]";
const VALUE_CLASSES = "flex items-center justify-end text-sm font-medium";
const SELECT_CONTROL_STYLE = {
  backgroundColor: "#F8F9FB",
  borderColor: "#EAEDF3A3",
};
const TIMER_BUTTON_CLASSES = "px-4 flex items-center";

const getClampedPercent = (value: number): number =>
  Math.min(Math.max(value, 0), 100);

const getBudgetUsedPercent = (
  trackedMinutes: number,
  budgetedMinutes: number,
): number => {
  if (!budgetedMinutes) {
    return 0;
  }

  return Math.round((trackedMinutes / budgetedMinutes) * 100);
};

const getBudgetColor = (usedPercent: number): string => {
  if (usedPercent <= 60) {
    return "#2FC66E";
  }

  if (usedPercent <= 80) {
    return "#F4C20D";
  }

  return "#EF3B3B";
};

const formatBudgetMinutes = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${hours}h ${minutes.toString().padStart(2, "0")}m`;
};

const getBudgetedMinutes = (timeBudget: number): number => {
  if (!Number.isFinite(timeBudget)) {
    return 0;
  }

  return Math.max(Math.round(timeBudget * 60), 0);
};

const getTrackedMinutes = (duration?: number | null): number => {
  const durationSeconds = Number(duration);

  if (!Number.isFinite(durationSeconds)) {
    return 0;
  }

  return Math.max(Math.round(durationSeconds / 60), 0);
};

const getRoleOptions = (roles?: RoleModel[]): SelectOptionType[] =>
  (roles || []).map((role) => ({
    value: role.id,
    label: role.roleName,
  }));

const getSelectedRoleOption = (
  roles: RoleModel[] | undefined,
  roleId: string | null,
): SelectOptionType | null => {
  const selectedRole = roles?.find((role) => role.id === roleId);

  if (!selectedRole) {
    return null;
  }

  return {
    value: selectedRole.id,
    label: selectedRole.roleName,
  };
};

const getSelectedRole = (
  roles: RoleModel[] | undefined,
  roleId: string | null,
): RoleModel | undefined => roles?.find((role) => role.id === roleId);

const getSelectedRoleUser = (
  role: RoleModel | null,
  roleAssigneeId: string | null,
): UserRoleModel | undefined =>
  role?.userRoles?.find((userRole) => userRole.userId === roleAssigneeId);

const getLatestTaskDueDate = (
  workTasks?: WorkTaskModel[],
): Date | undefined => {
  if (!workTasks?.length) {
    return undefined;
  }

  return workTasks
    .map((task) => new Date(task.dueDate))
    .reduce((latestDate, currentDate) =>
      currentDate > latestDate ? currentDate : latestDate,
    );
};

const getProgressWidth = (workTasks?: WorkTaskModel[]): number => {
  if (!workTasks?.length) {
    return 0;
  }

  return Math.trunc(100 / (workTasks.length / getCompletedTasks(workTasks)));
};

const getWorkAssignees = (work: WorkDetailsModel): WorkJobAssignee[] => {
  if (work.jobAssignees?.length) {
    return work.jobAssignees.filter((assignee) => assignee.assignedUser);
  }

  if (!work.assignedUser) {
    return [];
  }

  return [
    {
      jobId: work.id,
      assignedUserId: work.assignedUserId,
      assignedUser: work.assignedUser,
      receiveEmailNotification: true,
      id: work.assignedUserId,
      relayId: null,
      createdAt: work.createdAt,
      createdTimeUTC: null,
      editedTimeUTC: null,
    },
  ];
};

const getAssigneeItems = (assignees: WorkJobAssignee[]): AvatarStackItem[] =>
  assignees.map((assignee) => ({
    id: assignee.id,
    label: getInitials(
      assignee.assignedUser.userProfile.firstName,
      assignee.assignedUser.userProfile.lastName,
    ),
    name: `${assignee.assignedUser.userProfile.firstName || ""} ${
      assignee.assignedUser.userProfile.lastName || ""
    }`.trim(),
    color: assignee.assignedUser.userProfile.color,
  }));

const areStringArraysEqual = (first: string[], second: string[]): boolean => {
  if (first.length !== second.length) {
    return false;
  }

  const firstSorted = [...first].sort();
  const secondSorted = [...second].sort();

  return firstSorted.every((item, index) => item === secondSorted[index]);
};

export const WorkDetailsInfo: FC<Props> = ({ work, setIsEdit }) => {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.appReducer.user);
  const { timer, startWorkTimer, pauseActiveTimer } = useTimerTarget();

  const orgId = getLinkedFirm()?.orgId;
  const startDate = moment(work.startDate).toDate();
  const dueDate = moment(work.dueDate).toDate();
  const defaultWorkDatePayload = {
    workId: work.id,
    createdAt: work.createdAt,
    relayId: work.relayId,
  };

  const { data: users } = useGetUsersQuery(orgId || "");
  const { data: roles, isLoading: isLoadingRoles } = useGetRolesQuery(
    { orgId: orgId || "" },
    { skip: !orgId },
  );
  const { data: workTasks } = useGetWorkTasksQuery({
    workId: work.id || "",
  });
  const [updateWorkAssignees, { isLoading: isUpdatingAssignees }] =
    useUpdateWorkAssigneesMutation();
  const [updateWorkRole, { isLoading: isUpdatingRole }] =
    useUpdateWorkRoleMutation();
  const [updateWorkDates, { data: updatedWork }] = useUpdateWorkDatesMutation();

  const [previewDates, setPreviewDates] = useState<WorkDateRange | null>(null);
  // WITEM-007 (QA batch 17): a refused typed date also says why under the
  // boxes (the toast alone was easy to miss).
  const [dateProblem, setDateProblem] = useState<string | null>(null);
  const [isOpenAddTimer, setIsOpenAddTimer] = useState(false);
  const [isOpenReassign, setIsOpenReassign] = useState(false);
  const [isOpenEditBudget, setIsOpenEditBudget] = useState(false);
  const [isRoleActionHovered, setIsRoleActionHovered] = useState(false);
  const [draftAssigneeIds, setDraftAssigneeIds] = useState<string[]>(() =>
    getWorkAssignees(work).map((assignee) => assignee.assignedUserId),
  );

  const roleOptions = useMemo<SelectOptionType[]>(
    () => getRoleOptions(roles),
    [roles],
  );
  const selectedRoleOption = useMemo<SelectOptionType | null>(
    () => getSelectedRoleOption(roles, work.roleId),
    [roles, work.roleId],
  );
  const selectedRole = useMemo<RoleModel | undefined>(
    () => getSelectedRole(roles, work.roleId),
    [roles, work.roleId],
  );
  const selectedUserRole = useMemo<UserRoleModel | undefined>(
    () => getSelectedRoleUser(selectedRole || null, work.roleAssigneeId),
    [work.roleAssigneeId, selectedRole],
  );
  const renderRoleUsersPreview = useCallback(
    (option: SelectOptionType) => {
      const role = getSelectedRole(roles, option.value);

      return <RoleMembersPreview role={role} />;
    },
    [roles],
  );

  const shownDates = (): WorkDateRange => {
    if (
      moment(updatedWork?.startDate).toDate() === startDate &&
      moment(updatedWork?.dueDate).toDate() === dueDate
    ) {
      return { startDate, dueDate };
    }

    if (previewDates) {
      return previewDates;
    }

    if (updatedWork) {
      return {
        startDate: moment(updatedWork.startDate).toDate(),
        dueDate: moment(updatedWork.dueDate).toDate(),
      };
    }

    return { startDate, dueDate };
  };

  const minDueDate = getLatestTaskDueDate(workTasks);
  const progressWidth = getProgressWidth(workTasks);
  const completedTasksCount = getCompletedTasks(workTasks || []);
  const workTasksCount = workTasks?.length || 0;
  const budgetedMinutes = getBudgetedMinutes(work.timeBudget);
  const trackedMinutes = getTrackedMinutes(work.duration);
  const budgetUsedPercent = getBudgetUsedPercent(
    trackedMinutes,
    budgetedMinutes,
  );
  const budgetProgressWidth = getClampedPercent(budgetUsedPercent);
  const budgetRemainingMinutes = Math.max(budgetedMinutes - trackedMinutes, 0);
  const budgetColor = getBudgetColor(budgetUsedPercent);
  const workTimerTarget = { type: "work" as const, workId: work.id };
  const isAnyTimerActive = isTimerActive(timer);
  const isCurrentWorkTimerActive = isTimerForTarget(timer, workTimerTarget);
  const isTimerBlockedByAnotherTarget =
    isAnyTimerActive && !isCurrentWorkTimerActive;
  const currentDates = shownDates();
  const workAssignees = getWorkAssignees(work);
  const workAssigneeIds = workAssignees.map(
    (assignee) => assignee.assignedUserId,
  );
  const assigneeItems = getAssigneeItems(workAssignees);
  const isDraftAssigneesChanged = !areStringArraysEqual(
    draftAssigneeIds,
    workAssigneeIds,
  );
  const canAssignRoleToMe = !!selectedRole?.userRoles.some(
    (userRole) => userRole.userId === user.id,
  );
  const isRoleAssigned = !!work.roleAssigneeId;
  const isRoleActionDisabled = !isRoleAssigned && !canAssignRoleToMe;
  const roleActionButtonVariant = isRoleAssigned
    ? isRoleActionHovered
      ? "danger-secondary"
      : "success-secondary"
    : "secondary";
  const roleActionTooltipContent = (() => {
    if (isRoleAssigned) {
      return "Unassign user from this role";
    }

    if (!work.roleId) {
      return "Select a role first";
    }

    if (!canAssignRoleToMe) {
      return "You are not a member of this role";
    }

    return "Assign this role to me";
  })();
  const roleActionIcon =
    isRoleAssigned && isRoleActionHovered ? (
      <CloseSvg />
    ) : isRoleAssigned ? (
      <CheckSvg />
    ) : (
      <IndividualContactSvg className={"h-6 w-6 stroke-current"} />
    );

  const closeAddTimer = () => {
    setIsOpenAddTimer(false);
  };

  const openEditBudget = () => {
    setIsOpenEditBudget(true);
  };

  const closeEditBudget = () => {
    setIsOpenEditBudget(false);
  };

  const refuseDate = (message: string) => {
    setDateProblem(message);
    toast.error(message, { toastId: "work-date-refused" });
  };

  const onChangeStartDate = (date: Date) => {
    const nextStartDate = moment(date).toDate();
    const due = currentDates.dueDate;
    if (due && moment(nextStartDate).isAfter(due, "day")) {
      refuseDate(START_AFTER_DUE);
      return;
    }
    setDateProblem(null);

    setPreviewDates({ startDate: nextStartDate, dueDate });
    updateWorkDates({
      ...defaultWorkDatePayload,
      startDate: nextStartDate,
      endDate: work.dueDate,
    });
  };

  const onChangeDueDate = (date: Date) => {
    const nextDueDate = moment(date).toDate();
    const start = currentDates.startDate;
    if (start && moment(nextDueDate).isBefore(start, "day")) {
      refuseDate(DUE_BEFORE_START);
      return;
    }
    if (minDueDate && moment(nextDueDate).isBefore(minDueDate, "day")) {
      refuseDate(DUE_BEFORE_SUBTASK);
      return;
    }
    setDateProblem(null);

    setPreviewDates({ startDate, dueDate: nextDueDate });
    updateWorkDates({
      ...defaultWorkDatePayload,
      startDate: work.startDate,
      endDate: nextDueDate,
    });
  };

  const onClickStopTimer = () => {
    if (!isCurrentWorkTimerActive) {
      return;
    }

    pauseActiveTimer();
    setIsOpenAddTimer(true);
  };

  const onClickStartTimer = () => {
    if (isAnyTimerActive) {
      return;
    }

    startWorkTimer({ work });
  };

  const toggleReassignMenu = () => {
    if (!isOpenReassign) {
      setDraftAssigneeIds(workAssigneeIds);
    }

    setIsOpenReassign(!isOpenReassign);
  };

  const closeReassignMenu = () => {
    setDraftAssigneeIds(workAssigneeIds);
    setIsOpenReassign(false);
  };

  const onClickReassignMenu = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  const onToggleDraftAssignee = (assigneeId: string) => {
    setDraftAssigneeIds((currentIds) => {
      if (currentIds.includes(assigneeId)) {
        return currentIds.filter((item) => item !== assigneeId);
      }

      if (currentIds.length >= MAX_ASSIGNEES) {
        return currentIds;
      }

      return [...currentIds, assigneeId];
    });
  };

  const onSaveAssignees = async () => {
    if (
      draftAssigneeIds.length === 0 ||
      draftAssigneeIds.length > MAX_ASSIGNEES ||
      !isDraftAssigneesChanged ||
      isUpdatingAssignees
    ) {
      return;
    }

    try {
      await updateWorkAssignees({
        jobId: work.id,
        assignedByUserId: work.assignedByUserId || user.id,
        userIds: draftAssigneeIds,
      }).unwrap();

      toast.success("Work assignees updated successfully");
      setIsOpenReassign(false);
    } catch {
      toast.error("Failed to update work assignees");
    }
  };

  const onChangeRole = (option: SelectOptionType) => {
    if (isUpdatingRole) {
      return;
    }

    updateWorkRole({
      workId: work.id,
      roleAssigneeId: null,
      roleId: option.value,
    });
  };

  const onClickAssignRoleToMe = () => {
    if (
      isUpdatingRole ||
      !work.roleId ||
      (!isRoleAssigned && !canAssignRoleToMe)
    ) {
      return;
    }

    updateWorkRole({
      workId: work.id,
      roleAssigneeId: isRoleAssigned ? null : user.id,
      roleId: work.roleId,
    });
  };

  const navigateToWorkContact = () => {
    navigate(
      navigateToContact({
        type: work.customer?.businessContactId ? "organization" : "contact",
        customerId: work.customerId || "",
        id: work.customer?.businessContactId || "",
      }),
    );
  };

  return (
    <Card extraClasses={CARD_CLASSES}>
      <div>
        <div className={"flex justify-between pt-4"}>
          <div className={"text-base font-semibold"}>Details</div>
          <div
            className={
              "flex cursor-pointer items-center font-notoSans text-base font-medium text-[#7B66F0]"
            }
            onClick={() => setIsEdit(true)}>
            Edit
          </div>
        </div>
        <div className={"border-b border-gray-200 py-2"}>
          <div
            className={
              "grid grid-cols-[max-content_minmax(0,1fr)] items-center gap-x-4 gap-y-4 py-4 text-base"
            }>
            <div className={"whitespace-nowrap text-sm text-[#2B333C]"}>
              Status
            </div>
            <div className={"flex justify-end"}>
              <WorkStatus work={work} positionType={"right-bottom"} />
            </div>
            <div className={LABEL_CLASSES}>Status Changed</div>
            <div
              className={"flex justify-end text-sm font-medium text-[#2B333C]"}>
              {work?.statusUpdatedAt
                ? moment(work?.statusUpdatedAt).format(DISPLAY_DATE_FORMAT)
                : "-"}
            </div>
            <div className={LABEL_CLASSES}>Task progress</div>
            <div className={"flex justify-end"}>
              <div
                className={
                  "w-fit rounded-md border border-[#EAEDF3A3] bg-[#F8F9FB] px-2 text-sm font-medium text-[#2B333C]"
                }>
                {`${completedTasksCount}/${workTasksCount} Subtasks`}
              </div>
            </div>
          </div>
          <div className={"h-1.5 w-full rounded-md bg-[#F4F6F8]"}>
            <div
              className={"h-full rounded-md"}
              style={{
                width: `${progressWidth}%`,
                maxWidth: "100%",
                backgroundColor: TASK_PROGRESS_COLOR,
              }}
            />
          </div>
          <div className={"mt-6 border-t border-gray-200 pt-4"}>
            <div className={"flex items-center justify-between"}>
              <div className={"text-sm font-medium text-[#2B333C]"}>
                Time budget
              </div>
              <div className={"text-sm font-medium text-[#2B333C80]"}>
                {budgetUsedPercent}% used
              </div>
            </div>
            <div className={"mt-4 h-[6px] w-full rounded-md bg-[#F4F6F8]"}>
              <div
                className={"h-full rounded-md"}
                style={{
                  width: `${budgetProgressWidth}%`,
                  maxWidth: "100%",
                  backgroundColor: budgetColor,
                }}
              />
            </div>

            <div className={"mt-4 grid grid-cols-3 gap-1 text-sm xl:gap-3"}>
              <div
                onClick={openEditBudget}
                className={
                  "cursor-pointer rounded-lg border border-gray-300 bg-gray-50 py-2 hover:underline"
                }>
                <div
                  className={
                    "text-center text-xs font-medium text-[#2B333C80]"
                  }>
                  Budgeted
                </div>
                <div className={"text-center font-semibold text-[#2B333C]"}>
                  {formatBudgetMinutes(budgetedMinutes)}
                </div>
              </div>
              <div
                className={
                  "rounded-lg border border-gray-300 bg-gray-50 py-2 text-center"
                }>
                <div
                  className={
                    "text-center text-xs font-medium text-[#2B333C80]"
                  }>
                  Tracked
                </div>
                <div className={"text-center font-semibold text-[#7C66F0]"}>
                  {formatBudgetMinutes(trackedMinutes)}
                </div>
              </div>
              <div
                className={
                  "rounded-lg border border-gray-300 bg-gray-50 py-2 text-right "
                }>
                <div
                  className={
                    "text-center text-xs font-medium text-[#2B333C80]"
                  }>
                  Remaining
                </div>
                <div
                  className={"text-center font-semibold"}
                  style={{ color: budgetColor }}>
                  {formatBudgetMinutes(budgetRemainingMinutes)}
                </div>
              </div>
            </div>
            <div className={"mt-4 grid grid-cols-[2fr_1fr] gap-3"}>
              {/* A disabled button shows no tooltip, so the hint sits on a wrapper (WITEM-025). */}
              <span
                className={"block"}
                title={
                  isTimerBlockedByAnotherTarget
                    ? "Another timer is running. Stop it first."
                    : undefined
                }>
                <Button
                  type={"button"}
                  size={"sm"}
                  fullWidth
                  leftIcon={
                    <FontAwesomeIcon icon={faPlay} className={"text-[10px]"} />
                  }
                  onClick={onClickStartTimer}
                  disabled={isAnyTimerActive}
                  className={TIMER_BUTTON_CLASSES}>
                  {isCurrentWorkTimerActive
                    ? "Timer running..."
                    : "Start timer"}
                </Button>
              </span>
              <Button
                type={"button"}
                size={"sm"}
                fullWidth
                variant={"danger-secondary"}
                leftIcon={
                  <FontAwesomeIcon icon={faStop} className={"text-[10px]"} />
                }
                onClick={onClickStopTimer}
                disabled={
                  !isCurrentWorkTimerActive || isTimerBlockedByAnotherTarget
                }
                className={`${TIMER_BUTTON_CLASSES} disabled:bg-[#F1F2F5] disabled:text-[#9AA1AE]`}>
                Stop
              </Button>
            </div>
          </div>
        </div>

        <div className={"border-b border-gray-200"}>
          <div className={SECTION_GRID_CLASSES}>
            <div className={LABEL_CLASSES}>Assignee</div>
            <div
              className={"relative flex items-center justify-end"}
              onClick={toggleReassignMenu}>
              <div className={"cursor-pointer"}>
                <AvatarStack
                  items={assigneeItems}
                  withPreview
                  previewTitle={"Assignees:"}
                  isPreviewDisabled={isOpenReassign}
                />
              </div>
              {isOpenReassign && (
                <div
                  onClick={onClickReassignMenu}
                  className={
                    "absolute right-0 top-9 z-[3] flex max-h-[280px] w-[245px] flex-col space-y-[2px] overflow-hidden rounded-[10px] border-[1px] border-gray-300 bg-white text-[13px] shadow-lg"
                  }>
                  <div
                    className={
                      "link-primary border-b-[1px] border-b-primary px-4 py-2 text-[14px]"
                    }>
                    Reassign work to:
                  </div>
                  <div className={"px-4 pt-2 text-xs text-[#8B98A8]"}>
                    {draftAssigneeIds.length} / {MAX_ASSIGNEES} selected
                  </div>
                  <div className={"overflow-y-auto"}>
                    {users?.map((item) => {
                      const isSelected = draftAssigneeIds.includes(item.id);
                      const isDisabled =
                        !isSelected && draftAssigneeIds.length >= MAX_ASSIGNEES;

                      return (
                        <label
                          className={
                            "has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50 has-[:disabled]:hover:bg-white has-[:disabled]:hover:text-inherit mt-2 flex cursor-pointer items-center space-x-[4px] px-4 last-of-type:mb-2 hover:bg-blue-400 hover:text-white"
                          }
                          key={item?.id}>
                          <input
                            type={"checkbox"}
                            checked={isSelected}
                            disabled={isDisabled}
                            onChange={() => onToggleDraftAssignee(item.id)}
                            className={
                              "checkbox mr-1 scale-75 rounded-[5px] border-gray-300 [--chkbg:--b1] [--chkfg:--a] checked:border-gray-300 checked:bg-base-100"
                            }
                          />
                          <AvatarPlaceholder
                            size={"2xs"}
                            type={"custom"}
                            avatarCustomColor={item.userProfile.color}
                            label={getInitials(
                              item?.userProfile?.firstName || "",
                              item?.userProfile?.lastName || "",
                            )}
                          />
                          <div>{`${item.userProfile?.firstName} ${item.userProfile?.lastName}`}</div>
                        </label>
                      );
                    })}
                  </div>
                  <div
                    className={
                      "grid grid-cols-2 gap-2 border-t border-gray-200 p-2"
                    }>
                    <LegacyButton
                      fullWidth
                      label={"Cancel"}
                      size={"custom"}
                      colorType={"outline"}
                      extraClasses={"btn-xs"}
                      buttonType={"button"}
                      onClick={closeReassignMenu}
                    />
                    <LegacyButton
                      fullWidth
                      label={"Save"}
                      size={"custom"}
                      extraClasses={"btn-xs"}
                      buttonType={"button"}
                      onClick={onSaveAssignees}
                      isLoading={isUpdatingAssignees}
                      disabled={
                        draftAssigneeIds.length === 0 ||
                        draftAssigneeIds.length > MAX_ASSIGNEES ||
                        !isDraftAssigneesChanged ||
                        isUpdatingAssignees
                      }
                    />
                  </div>
                </div>
              )}
            </div>

            <div className={LABEL_CLASSES}>Role</div>
            <div className={"flex w-full justify-end gap-2"}>
              <div className={"min-w-[96px]"}>
                <CustomSelect
                  options={roleOptions}
                  onChange={(option) =>
                    onChangeRole(option as SelectOptionType)
                  }
                  value={selectedRoleOption}
                  indicatorIcon={() => <ChevronDown size={16} />}
                  placeholder={
                    isLoadingRoles || isUpdatingRole ? "Loading..." : "Select"
                  }
                  isLoading={isLoadingRoles || isUpdatingRole}
                  withMenu
                  renderOptionPreview={renderRoleUsersPreview}
                  selectStyle={{
                    small: true,
                    controlStyle: SELECT_CONTROL_STYLE,
                    fontWeight: 500,
                  }}
                />
              </div>
              <span
                className={"inline-flex"}
                data-tooltip-id={ASSIGN_ROLE_TO_ME_TOOLTIP_ID}
                data-tooltip-content={roleActionTooltipContent}>
                <Button
                  iconOnly
                  size={"sm"}
                  variant={roleActionButtonVariant}
                  leftIcon={roleActionIcon}
                  onClick={onClickAssignRoleToMe}
                  onMouseEnter={() => setIsRoleActionHovered(true)}
                  onMouseLeave={() => setIsRoleActionHovered(false)}
                  disabled={isRoleActionDisabled}
                  loading={isUpdatingRole}
                />
              </span>
              <ReactTooltip
                id={ASSIGN_ROLE_TO_ME_TOOLTIP_ID}
                place={"bottom"}
                className={"!z-10 !px-2 !py-1 !text-left !text-xs !font-medium"}
              />
            </div>

            {/* The role's person, only when one is set (no empty row). */}
            {work.roleAssigneeId && (
              <>
                <div></div>
                <div className={"flex w-full justify-end"}>
                  <div className={"flex items-center gap-2"}>
                    <AvatarPlaceholder
                      size={"2xs"}
                      type={"custom"}
                      avatarCustomColor={
                        selectedUserRole?.user?.userProfile?.color
                      }
                      label={getInitials(
                        selectedUserRole?.user?.userProfile?.firstName || "",
                        selectedUserRole?.user?.userProfile?.lastName || "",
                      )}
                    />
                    <div className={"text-sm"}>
                      {selectedUserRole?.user?.userProfile?.firstName || ""}{" "}
                      {selectedUserRole?.user?.userProfile?.lastName || ""}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className={"border-b border-gray-200 py-4"}>
          <RecurringCard work={work} />
        </div>

        <div className={"border-b border-gray-200"}>
          <div className={SECTION_GRID_CLASSES}>
            <div className={LABEL_CLASSES}>Start date</div>
            <div className={"flex w-full min-w-0 justify-end"}>
              <div className={"flex w-full min-w-0 items-center justify-end"}>
                <StyledDatePicker
                  value={currentDates.startDate}
                  onChange={onChangeStartDate}
                  maxDate={currentDates.dueDate}
                  onOutOfRange={() => refuseDate(START_AFTER_DUE)}
                  format={DATE_PICKER_FORMAT}
                  styleType={"ghost"}
                  width={"100%"}
                  textAlign={"end"}
                  fontWeight={"500"}
                  fontSize={"14px"}
                  color={"#2B333C"}
                />
              </div>
            </div>

            <div className={LABEL_CLASSES}>Due date</div>
            <div className={"flex w-full min-w-0 justify-end"}>
              <div className={"flex w-full min-w-0 items-center justify-end"}>
                <StyledDatePicker
                  value={currentDates.dueDate}
                  onChange={onChangeDueDate}
                  styleType={"ghost"}
                  minDate={laterDate(currentDates.startDate, minDueDate)}
                  onOutOfRange={(date) =>
                    refuseDate(
                      currentDates.startDate &&
                        moment(date).isBefore(currentDates.startDate, "day")
                        ? DUE_BEFORE_START
                        : DUE_BEFORE_SUBTASK,
                    )
                  }
                  format={DATE_PICKER_FORMAT}
                  width={"100%"}
                  textAlign={"end"}
                  fontWeight={"500"}
                  fontSize={"14px"}
                  color={"#2B333C"}
                />
              </div>
            </div>
          </div>
          {dateProblem && (
            <div
              role={"alert"}
              className={"pb-3 text-[12.5px] font-medium text-[#E5484D]"}>
              {dateProblem}
            </div>
          )}
        </div>

        <div className={"border-b border-gray-200"}>
          <div className={SECTION_GRID_CLASSES}>
            <div className={LABEL_CLASSES}>Client</div>
            <div className={"flex items-center justify-end gap-2"}>
              <div className={"cursor-pointer"} onClick={navigateToWorkContact}>
                <AvatarPlaceholder
                  type={work?.customer?.businessContactId ? "purple" : "blue"}
                  size={"2xs"}
                  label={getInitialsFromFullName(work?.customer?.name)}
                />
              </div>
              <div
                className={"cursor-pointer text-sm font-medium hover:underline"}
                onClick={navigateToWorkContact}>
                {work?.customer?.name}
              </div>
            </div>
            <div className={LABEL_CLASSES}>Work type</div>
            <div className={VALUE_CLASSES}>{work?.service?.name}</div>
          </div>
        </div>
        <div>
          {isOpenAddTimer && (
            <AddTimer isOpen={isOpenAddTimer} closeModal={closeAddTimer} />
          )}
          {isOpenEditBudget && (
            <EditBudgetModal
              work={work}
              workTasks={workTasks}
              onClose={closeEditBudget}
            />
          )}
        </div>
      </div>
    </Card>
  );
};
